const DelegateProtocol = require("./delegate").DelegateProtocol;
const TemplateRenderer = require("../templaterenderer").TemplateRenderer;
const $ = require("../jquery_with_plugins");

/**
 * @typedef {Object} SearchViewOptions This class represents the parameters for SearchView {@link SearchView}.
 * @example Template sample
 *
 * ```html
 * <div>
 *      <input class="woosmap-search-input"/>
 *      <span class="woosmap-search-clear">✕</span>
 *      <span class="woosmap-my-location-button">My location</span>
 *      <button class="woosmap-search-button">Search</button>
 * <div>
 * ```
 *
 * @example CSS classes purpose
 *
 * - `woosmap-search-input` The input used to fill the search query
 * - `woosmap-search-clear` The element used to clear the search input
 * - `woosmap-search-button` The element used to trigger the search on click.
 * - `woosmap-my-location-button` The element used to trigger geolocation based search
 *
 * Additionaly the search is triggered when the search input is validated (ie: Enter key is pressed).
 *
 * @example Location template sample
 *
 * ```html
 * <div>
 *      {{formattedAddress}}
 * </div>
 * ```
 *
 * @property {string} [template] Search input template.
 * @property {string} [resultsTemplate] location results template.
 * @property {string} [geolocText] text to display on input field after a geolocation
 *
 * @memberOf woosmap.ui
 */

/**
 * Constructs a SearchView
 * ## Delegates
 *
 * - `didSearchClear` is called when the search query is validated.
 *
 * @constructor
 * @extends woosmap.utils.DelegateProtocol
 * @param {SearchViewOptions} [searchViewOptions]  options for the searchView
 * @memberOf woosmap.ui
 */
class SearchView extends DelegateProtocol {
    constructor(searchViewOptions) {
        super();
        const _searchViewOptions = searchViewOptions || {};
        if (typeof _searchViewOptions === "string") {
            this._renderer = new TemplateRenderer(_searchViewOptions);
            this._resultsRender = new TemplateRenderer("");
        } else {
            this._renderer = new TemplateRenderer(_searchViewOptions.template);
            this._resultsRender = new TemplateRenderer(_searchViewOptions.resultsTemplate);
            this._geolocText = _searchViewOptions.geolocText || "My Location";
        }

        this.$div = $('<div class="woosmap-search-container search_container"></div>');

        this.$div.append(this._renderer.render());

        this.$searchInput = this.$div.find(".search_input, .woosmap-search-input");
        this.$searchClear = this.$div.find(".search_clear, .woosmap-search-clear");
        this.$searchButton = this.$div.find(".search_button, .woosmap-search-button");
        this.$myLocationButton = this.$div.find(".woosmap-my-location-button");

        this.$resultsSelector = $('<div class="woosmap-multi-results-selector">');

        this.setupInteraction();
        this.query = null;
        this.autocomplete_query = null;
        this.selectedStore = null;

        this.delegate = null;
        this._timer_value = 300;
        this.first_use = true;

        this.locationResults = [];
        this._isLocationResultsDisplaying = false;
        this._selectedLocationResultIndex = 0;
        this.location = {};
    }

    /**
     * Setups the interactions with the input, button, and clear button
     */
    setupInteraction() {
        function _is_not_a_arrow_key(keyCode) {
            return !(keyCode > 37 && keyCode < 41);
        }

        if (this.$myLocationButton) {
            this.$myLocationButton.click(() => {
                const locationProvider = new woosmap.location.LocationProvider();
                locationProvider.bindTo("location", this);
                this.$searchInput.attr("disabled", "disabled");
                this.$searchInput.val(this._geolocText);
                this.$searchClear.show();
                this.$myLocationButton.hide();
                locationProvider.askForLocation(navigator.geolocation);
            });
        }

        this.$searchInput.focusin(() => {
            if (this.$searchInput.val().length > 0) {
                this.$searchClear.show();
            }
        });

        if (this.$searchButton) {
            this.$searchButton.click(() => {
                this.setSearchQueryWithInputValue("query");
            });
        }

        if (this.$searchClear) {
            this.$searchClear.click(
                function () {
                    this.clearResults();
                }.bind(this),
            );
        }

        this.$searchInput.keyup((event) => {
            if (this.first_use) {
                this.first_use = false;
            }
            if (event.keyCode === 13) {
                if (this._isLocationResultsDisplaying) {
                    this.chooseLocation(this._selectedLocationResultIndex);
                    event.stopPropagation();
                } else {
                    this.setSearchQueryWithInputValue("query");
                }
            } else if (_is_not_a_arrow_key(event.keyCode)) {
                this.hideLocationResults();
                this.$myLocationButton.hide();
                this.$searchClear.show();
                if (this._timer) {
                    clearTimeout(this._timer);
                }
                this._timer = setTimeout(() => {
                    this._timer = null;
                    this.setSearchQueryWithInputValue("autocomplete_query");
                }, this._timer_value);
            } else if (this._isLocationResultsDisplaying) {
                event.stopPropagation();
                this.switchLocationResult(event.keyCode);
            }
        });
    }

    /**
     * Sets the query property with the value of the input.
     * @param {String} property
     */
    setSearchQueryWithInputValue(property) {
        const address = this.$searchInput.val();
        if (!address && this.isPropertyBound("autocomplete_query")) {
            this.clearResults();
        } else {
            this.set(property, address);
        }
    }

    /**
     * Method called when searchView need to clear its results.
     */
    clearResults() {
        this.clearInput();
        this.set("query", null);
        this.set("autocomplete_query", null);
        this.callDelegateMethod("didClearSearch");
        this.$myLocationButton.show();
    }

    /**
     * Method called when searchView need to clear its input.
     */
    clearInput() {
        this.$searchClear.hide();
        this.$searchInput.val("");
        this.$searchInput.attr("disabled", false);
    }

    /**
     *
     * @returns {*} the containing element.
     */
    getContainer() {
        return this.$div[0];
    }

    /**
     * Method called when locationResults changed.
     *
     * When changed is trigger searchView will display location results into resultSelector container.
     * @type {Function}
     */
    locationResults_changed() {
        this.displayLocationResults(this.get("locationResults"));
    }

    /**
     * Display location's result to resultsSelector into container and add click listener.
     * @param {object} [locationResults] locationResults is a list containing lat, lng and formattedAddress
     * for each locations' result.
     * @private
     */
    displayLocationResults(locationResults) {
        const self = this;
        self.hideLocationResults();
        self.$resultsSelector.show();
        self._isLocationResultsDisplaying = true;
        for (let i = 0; i < locationResults.length; i++) {
            const locationChoiceDiv = woosmap.$(this._resultsRender.render(locationResults[i]));
            this.$resultsSelector.append(locationChoiceDiv);
            locationChoiceDiv.data("index", i);
            locationChoiceDiv.addClass("woosmap-location-choice");
            locationChoiceDiv.click(function () {
                self.chooseLocation(woosmap.$(this).data("index"));
            });
        }
    }

    /**
     * Set location property with locationResult corresponding to locationIndex
     * @param {Integer} [locationIndex]
     * @private
     */
    chooseLocation(locationIndex) {
        const locationResults = this.get("locationResults");
        if (locationResults && 0 <= locationIndex && locationIndex < locationResults.length) {
            const locationChoice = locationResults[locationIndex];
            this.set("location", {
                lat: locationChoice.lat,
                lng: locationChoice.lng,
            });
            this.$searchInput.val(locationChoice.formattedAddress);
            this.hideLocationResults();
        }
    }

    /**
     * Clean and hide resultsSelector from locations' result.
     * @private
     */
    hideLocationResults() {
        this.$resultsSelector.html("");
        this.$resultsSelector.hide();
        this._isLocationResultsDisplaying = false;
        this._selectedLocationResultIndex = -1;
    }

    /**
     * Return resultsSelector container.
     * @returns {*}
     */
    getResultsSelector() {
        return this.$resultsSelector;
    }

    /**
     * Update current location depending on keyCode parameters.
     * @param {Integer} [keyCode]
     * @private
     */
    switchLocationResult(keyCode) {
        $($(".woosmap-location-choice")[this._selectedLocationResultIndex]).removeClass("selected");
        if (keyCode === 38) {
            this._selectedLocationResultIndex--;
        } else if (keyCode === 40) {
            this._selectedLocationResultIndex++;
        }
        if (this._selectedLocationResultIndex >= this.get("locationResults").length) {
            this._selectedLocationResultIndex = 0;
        } else if (this._selectedLocationResultIndex < 0) {
            this._selectedLocationResultIndex = this.get("locationResults").length - 1;
        }
        $($(".woosmap-location-choice")[this._selectedLocationResultIndex]).addClass("selected");
    }
}

exports.SearchView = SearchView;
